import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import {
  QaiButtonComponent,
  QaiFieldComponent,
  QaiFloatingSelectComponent,
  QaiFloatingSelectOption,
} from '@qai/ui';
import { DetailsSectionComponent } from '../../../components/details-container/details-section.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { ISkill } from 'src/app/interfaces/self-serve/iskill';

// General tab "Scoring" section for Knowledge Check (GUIDED) calls. The skills rubric is optional;
// selecting one links it to the call via updateSkillSelection. Unlike roleplay, no scoring
// refinement is requested afterwards.
@Component({
  selector: 'knowledge-check-scoring-section',
  standalone: true,
  templateUrl: './knowledge-check-scoring-section.component.html',
  styleUrls: ['./knowledge-check-scoring-section.component.scss'],
  imports: [
    TranslocoModule,
    QaiButtonComponent,
    QaiFieldComponent,
    QaiFloatingSelectComponent,
    DetailsSectionComponent,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class KnowledgeCheckScoringSectionComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorService = inject(AuthorService);
  private readonly authorReactiveFormService = inject(AuthorReactiveFormService);

  callUuid = '';
  rubricOptions: QaiFloatingSelectOption[] = [];
  rubricControl?: FormControl;
  /** Published Knowledge Checks can't be edited (the backend rejects skill selection updates). */
  readOnly = false;

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    if (!this.callUuid) return;

    this.readOnly = this.authorReactiveFormService.isReadOnly(this.callUuid);
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.rubricControl = form.get(`${SimulationConstants.SKILL_PROMPT_PATH}.rubric`) as FormControl;
      });

    this.loadRubricOptions();
  }

  onRubricChange(value: string | undefined): void {
    if (this.readOnly) return;
    const skillUuid = value || '';
    if ((this.rubricControl?.value || '') === skillUuid) return;

    this.rubricControl?.setValue(skillUuid);
    this.authorReactiveFormService.patchForm(this.callUuid, SimulationConstants.SKILL_PROMPT_PATH, {
      rubric: skillUuid,
    });
    this.authorService
      .updateSkillSelection(this.callUuid, skillUuid || null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err) => console.error('Failed to update skill selection:', err),
      });
  }

  private loadRubricOptions(): void {
    this.authorService
      .findActiveSkillsByAccount(this.callUuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (skills: ISkill[]) => {
          this.rubricOptions = skills.map((skill) => ({ value: skill.uuid, label: skill.name }));
        },
        error: (err) => console.error('Failed to load skills rubrics:', err),
      });
  }
}
